import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/new-components/ui/button';
import { useKycGate } from '../components/new-components/KycGate';
import {
  TrendingUp,
  DollarSign,
  TrendingDown,
  Target,
  CheckCircle2,
} from 'lucide-react';
import { TENANT } from '../config';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { INVEST_NAV_ITEMS } from '../components/Layout/navConfig';
import api from '../services/api';
import { DocumentAccessNotice } from '../components/new-components/DocumentAccessNotice';
import { getIntroCallUrl, getProtectedDocumentNotice, openProtectedDocument, type DocumentAccessNotice as DocumentAccessNoticeType } from '../utils/protectedDocuments';

/* ─── Document type ───────────────────────────────────────────── */
interface DealDocument {
  id: string;
  title: string;
  filename: string;
  kind: string;
  file_size_bytes: string;
  mime_type: string;
  requires_esign: boolean;
  visible_to: string;
  download_url: string;
  created_at: string;
}

/* ─── Override types ──────────────────────────────────────────── */
interface HiveFundOverrides {
  heroHeading?: string;
  heroSubheading?: string;
  heroBody?: string;
  heroCtaText?: string;
  heroSecondaryCtaText?: string;
  aboutHeading?: string;
  aboutBody?: string;
  strategyHeading?: string;
  strategyIntro?: string;
  strategyPoints?: Array<{ title: string; description: string }>;
  marketsHeading?: string;
  marketsBody?: string;
  marketItems?: Array<{ title: string; description: string }>;
  whyWeWinHeading?: string;
  whyWeWinBody?: string;
  whyWeWinItems?: Array<{ title: string; description: string }>;
  benefitsHeading?: string;
  benefitsSubheading?: string;
  benefitItems?: Array<{ title: string; description: string }>;
  keyTermsHeading?: string;
  keyTermItems?: Array<{ label: string; value: string }>;
  howItWorksHeading?: string;
  howItWorksSubheading?: string;
  howItWorksSteps?: Array<{ title: string; description: string }>;
  ctaHeading?: string;
  ctaBody?: string;
  ctaText?: string;
  footerDisclaimer?: string;
}

function parseHiveFundContent(content: any): HiveFundOverrides {
  const overrides: HiveFundOverrides = {};
  const sections = content?.settings?.sections;
  if (!sections) return overrides;

  const h = sections.hero || {};
  if (h.heading) overrides.heroHeading = h.heading;
  if (h.subheading) overrides.heroSubheading = h.subheading;
  if (h.body) overrides.heroBody = h.body;
  if (h.ctaText) overrides.heroCtaText = h.ctaText;
  if (h.secondaryCtaText) overrides.heroSecondaryCtaText = h.secondaryCtaText;

  const a = sections.about || {};
  if (a.heading) overrides.aboutHeading = a.heading;
  if (a.body) overrides.aboutBody = a.body;
  if (a.strategyHeading) overrides.strategyHeading = a.strategyHeading;
  if (a.strategyIntro) overrides.strategyIntro = a.strategyIntro;
  if (Array.isArray(a.strategyPoints) && a.strategyPoints.length) overrides.strategyPoints = a.strategyPoints;

  const m = sections.markets || {};
  if (m.heading) overrides.marketsHeading = m.heading;
  if (m.body) overrides.marketsBody = m.body;
  if (Array.isArray(m.items) && m.items.length) overrides.marketItems = m.items;

  const w = sections.whyWeWin || {};
  if (w.heading) overrides.whyWeWinHeading = w.heading;
  if (w.body) overrides.whyWeWinBody = w.body;
  if (Array.isArray(w.items) && w.items.length) overrides.whyWeWinItems = w.items;

  const b = sections.benefits || {};
  if (b.heading) overrides.benefitsHeading = b.heading;
  if (b.subheading) overrides.benefitsSubheading = b.subheading;
  if (Array.isArray(b.items) && b.items.length) overrides.benefitItems = b.items;

  const kt = sections.keyTerms || {};
  if (kt.heading) overrides.keyTermsHeading = kt.heading;
  if (Array.isArray(kt.items) && kt.items.length) overrides.keyTermItems = kt.items;

  const hw = sections.howItWorks || {};
  if (hw.heading) overrides.howItWorksHeading = hw.heading;
  if (hw.subheading) overrides.howItWorksSubheading = hw.subheading;
  if (Array.isArray(hw.steps) && hw.steps.length) overrides.howItWorksSteps = hw.steps;

  const c = sections.cta || {};
  if (c.heading) overrides.ctaHeading = c.heading;
  if (c.body) overrides.ctaBody = c.body;
  if (c.ctaText) overrides.ctaText = c.ctaText;

  const f = sections.footer || {};
  if (f.disclaimer) overrides.footerDisclaimer = f.disclaimer;

  return overrides;
}

export function HiveFundPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'summary' | 'benefits' | 'documents' | 'financials'>('summary');
  const { requireKyc, KycGateModal } = useKycGate();
  const [overrides, setOverrides] = useState<HiveFundOverrides>({});
  const [dealDocs, setDealDocs] = useState<DealDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [documentNotice, setDocumentNotice] = useState<DocumentAccessNoticeType | null>(null);

  useEffect(() => {
    api.get('/site-pages/hive-fund')
      .then((res: any) => {
        const data = res?.data?.data || res?.data || res;
        if (data?.content) {
          setOverrides(parseHiveFundContent(data.content));
        }
      })
      .catch(() => { /* no published hive-fund page — use hardcoded defaults */ });

    // Fetch deal documents
    api.get('/offerings/hive-fund')
      .then((res: any) => {
        const deal = res?.data?.data || res?.data;
        if (deal?.id) {
          return api.get(`/offerings/${deal.id}/documents`);
        }
      })
      .then((res: any) => {
        const docs = res?.data?.data || res?.data;
        if (Array.isArray(docs)) setDealDocs(docs);
      })
      .catch(() => { /* no docs available */ })
      .finally(() => setDocsLoading(false));
  }, []);

  // Financial calculator state
  const [investmentAmount, setInvestmentAmount] = useState<number>(50000);
  const [holdPeriod, setHoldPeriod] = useState<number>(10);

    const handleRequestAccess = () => {
      window.open(getIntroCallUrl(), '_blank');
  };

  const handleInvestNow = () => {
      description: 'No loans or property hunting. Review key documents and complete investor verification before investing.'
  };

  // Financial projections calculator
  const calculateProjections = (amount: number, years: number) => {
    const bullRate = 0.20;
    const baseRate = 0.17;
    const bearRate = 0.12;

    const bullValue = amount * Math.pow(1 + bullRate, years);
    const baseValue = amount * Math.pow(1 + baseRate, years);
    const bearValue = amount * Math.pow(1 + bearRate, years);

    return {
      bull: { value: bullValue, gain: bullValue - amount, return: ((bullValue - amount) / amount) * 100 },
      base: { value: baseValue, gain: baseValue - amount, return: ((baseValue - amount) / amount) * 100 },
      bear: { value: bearValue, gain: bearValue - amount, return: ((bearValue - amount) / amount) * 100 }
    };
  };

  const projections = calculateProjections(investmentAmount, holdPeriod);

  const benefits = overrides.benefitItems || [
    {
      title: 'Cash Flow',
      description: 'Investors benefit from targeted 12% annual cash flow, distributed quarterly. Each property generates steady income through premium short-term rental operations.'
    },
    {
      title: 'Appreciation',
      description: 'Participate in equity growth with targeted 16-18% IRR, structured around an 8% preferred return followed by a 70/30 profit split (LP/GP).'
    },
    {
      title: 'Tax Advantages',
      description: 'Benefit from depreciation, cost segregation studies, deductible mortgage interest, property taxes, and operating expense write-offs.'
    },
    {
      title: 'Leverage Without Liability',
      description: 'Participate in leveraged real estate without personal debt exposure. All financing secured through established lending partners.'
    },
    {
      title: 'Diversification',
      description: 'Gain balanced exposure to multiple markets across Florida, the Southeast, and beyond, each selected for tourism demand and growth potential.'
    }
  ];

  const whyWeWin = overrides.whyWeWinItems || [
    {
      title: 'Hyper-Local Advantage',
      description: "We're based in Tampa, giving us boots-on-the-ground insight into the best-performing neighborhoods and micro-markets in Florida."
    },
    {
      title: 'Established Relationships',
      description: 'Direct partnerships with local brokers, contractors, and maintenance mean early access to off-market deals and faster, more reliable renovations.'
    },
    {
      title: 'Below-Market Acquisitions',
      description: "We source off-market deals you won't see on Zillow and have wholesale property management rates at a 50-65% discount."
    },
    {
      title: 'Proven Operations Playbook',
      description: 'Our existing portfolio includes 27 STRs in Florida with systems that consistently deliver 70%+ occupancy and premium nightly rates.'
    },
    {
      title: 'Tax-Advantaged Structure',
      description: 'Investors benefit from accelerated depreciation, often offsetting a large portion of year-one income.'
    },
    {
      title: 'Defined Value-Add & Exit Strategy',
      description: 'Every acquisition is chosen for immediate cash flow and a clear refinance or sale opportunity within 5-7 years.'
    }
  ];

  const investmentSteps = overrides.howItWorksSteps || [
    {
      number: '1',
      title: 'Simple Onboarding',
      description: 'No loans or property hunting. Just provide key documents and complete KYC verification.'
    },
    {
      number: '2',
      title: 'Fund Your Investment',
      description: 'Link your bank account or use crypto. Get diversified across high-performance short-term rentals.'
    },
    {
      number: '3',
      title: 'Quarterly Cashflow',
      description: 'Distributions begin in 3-6 months. Collect dividends straight to your account every quarter.'
    },
    {
      number: '4',
      title: 'The Exit',
      description: 'When properties sell, profits are distributed back to investors beyond quarterly cash flow.'
    }
  ];

  const keyTerms = overrides.keyTermItems || [
    { label: 'Preferred Return', value: '8%' },
    { label: 'Profit Split (LP/GP)', value: '70/30' },
    { label: 'Target IRR Range', value: '16-18%' },
    { label: 'Target Annual Cash Flow', value: '12%' }
  ];

  const markets = overrides.marketItems || [
    { title: 'Population & Job Growth', desc: 'Fueling sustained rental demand' },
    { title: 'Tourism-Driven Economies', desc: 'Supporting consistent occupancy and premium rates' },
    { title: 'STR-Friendly Policies', desc: 'Providing operational stability' },
    { title: 'Competitive Landscape', desc: 'Limited institutional saturation' }
  ];

  const strategyPoints = overrides.strategyPoints?.map(s => s.description || s.title) || [
    'Focus on mid- to high-end single-family and small multifamily homes in tourism-backed, high-growth markets',
    'Light to moderate renovations and premium amenities unlock significant value and sustained cash flow',
    'Consolidate multiple assets under one fund structure for operational efficiencies and reduced overhead',
    'Professional management with advanced pricing technology ensuring optimal occupancy and revenue'
  ];

  const formatFileSize = (bytes: string | number) => {
    const b = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
    if (!b || isNaN(b)) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1048576).toFixed(1)} MB`;
  };

  const handleDocDownload = async (doc: DealDocument) => {
    try {
      const url = doc.download_url.startsWith('http')
        ? doc.download_url
        : `${window.location.origin}${doc.download_url}`;
      await openProtectedDocument(url, doc.filename || doc.title);
      setDocumentNotice(null);
    } catch (error) {
      setDocumentNotice(getProtectedDocumentNotice(error));
    }
  };

  const investmentHighlights = [
    { label: 'Base Case (10-Year Hold)', value: `$${calculateProjections(100000, 10).base.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`, sub: 'on $100,000 investment' },
    { label: 'Target IRR Range', value: '16-18%', sub: 'annualized return' },
    { label: 'Preferred Return', value: '8%', sub: 'then 70/30 LP-GP split' }
  ];

  return (
    <div className="min-h-screen bg-background portal-typography">
      {/* Header - shared public header with navigation */}
      <PublicHeader navItems={INVEST_NAV_ITEMS} />

      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A] to-[#121212]">
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-4xl md:text-6xl lg:text-7xl mb-6 max-w-4xl mx-auto">
            {overrides.heroHeading || 'The Hive Fund'}
          </h1> 
          
          <p className="text-xl md:text-2xl text-white/90 mb-4 max-w-4xl mx-auto">
            {overrides.heroSubheading || 'Invest in a diversified portfolio of premium vacation rental properties'}
          </p>
          
          <p className="text-lg text-white/80 mb-10 max-w-3xl mx-auto">
            {overrides.heroBody || 'Access institutional-quality real estate investments with quarterly distributions, professional management, and growth potential in high-demand markets.'}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={handleRequestAccess}
              size="lg"
              className="text-base font-semibold px-8 w-full sm:w-auto"
            >
              Request Access
            </Button>
            <Button
              onClick={handleInvestNow}
              size="lg"
              className="text-base font-semibold px-10 w-full sm:w-auto bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
              >
              Invest Now
            </Button>
          </div>
        </div>
      </section>
      {/* Tab Navigation */}
      <section className="sticky top-[73px] z-40 bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 overflow-x-auto py-4">
            {[
              { id: 'summary', label: 'Summary' },
              { id: 'benefits', label: 'Benefits' },
              { id: 'documents', label: 'Documents' },
              { id: 'financials', label: 'Financials' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#F9EF23] text-[#242424]'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Summary Tab */}
      {activeTab === 'summary' && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-8">{overrides.aboutHeading || 'About The Hive Fund'}</h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  The Hive Fund offers investors the opportunity to participate in a diversified portfolio
                  of short-term rental properties across high-growth markets in the Southeast. By investing in the Hive Fund,
                  investors gain exposure to multiple properties and markets through a single investment combining the stability
                  of real estate with the income potential of hospitality assets.
                </p>
                <p>
                  New acquisitions will be added over time, expanding diversification and optimizing returns as the portfolio grows.
                  The Hive Fund is structured as a private real estate investment fund under Regulation D 506(c) and is professionally
                  managed by our team, leveraging data-driven analysis, value-add strategies, and hands-on operational
                  expertise to maximize performance.
                </p>
              </div>
            </div>

            {/* Investment Strategy */}
            <div className="mb-16">
              <h3 className="text-2xl font-display font-bold text-foreground mb-6">{overrides.strategyHeading || 'Investment Strategy'}</h3>
              <div className="space-y-4 text-muted-foreground">
                <p className="text-lg leading-relaxed">
                  Our strategy is designed to generate steady income and long-term appreciation by acquiring high-performing
                  short-term rental (STR) properties across the Southeast and beyond.
                </p>
                <ul className="space-y-3 ml-6">
                  {strategyPoints.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-[#242424] flex-shrink-0"/>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Markets */}
            <div className="mb-16">
              <h3 className="text-2xl font-display font-bold text-foreground mb-6">{overrides.marketsHeading || 'Markets'}</h3>
              <div className="space-y-4 text-muted-foreground">
                <p className="text-lg leading-relaxed">
                  The Hive Fund is building its foundation in the Tampa Bay metro while actively expanding across the Southeast's
                  strongest leisure and migration corridors.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  {markets.map((item, i) => (
                    <div key={i} className="bg-card border border-border rounded-lg p-6 hover:bg-primary/15 transition-colors">
                      <h4 className="text-lg font-semibold text-foreground mb-2">{item.title}</h4>
                      <p className="text-muted-foreground">{(item as any).desc || (item as any).description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Why We Win */}
            <div>
              <h3 className="text-2xl font-display font-bold text-foreground mb-6">{overrides.whyWeWinHeading || 'Why We Win'}</h3>
              <p className="text-lg text-muted-foreground mb-8">
                Our competitive edge comes from combining hyper-local expertise, exclusive deal flow, and a proven operational playbook.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {whyWeWin.map((item, index) => (
                  <div key={index} className="bg-card border border-border rounded-lg p-6 hover:bg-primary/15 transition-colors">
                    <h4 className="text-lg md:text-xl font-bold text-foreground mb-3">{item.title}</h4>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Benefits Tab */}
      {activeTab === 'benefits' && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">{overrides.benefitsHeading || 'Benefits Overview'}</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                {overrides.benefitsSubheading || 'Passive income, appreciation, and tax efficiency - all in one investment.'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-8 relative hover:bg-primary/15 transition-colors">
                  <div className="absolute top-6 right-6 text-6xl font-medium text-primary">
                    {index + 1}
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">{benefit.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>

            {/* Key Investment Terms */}
            <div className="bg-card border border-border rounded-lg p-8">
              <h3 className="text-2xl font-display font-bold text-foreground mb-6">{overrides.keyTermsHeading || 'Key Investment Terms'}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {keyTerms.map((item, i) => (
                  <div key={i}>
                    <p className="text-md font-semibold mb-2">{item.label}</p>
                    <p className="text-3xl md:text-4xl font-medium text-primary">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">Fund Documents</h2>
              <p className="text-xl text-muted-foreground">
                Essential documents for your investment review
              </p>
            </div>

            {docsLoading ? (
              <div className="text-center py-12 text-muted-foreground">Loading documents...</div>
            ) : dealDocs.length > 0 ? (
              <div className="space-y-4">
                {documentNotice && (
                  <DocumentAccessNotice notice={documentNotice} onDismiss={() => setDocumentNotice(null)} />
                )}
                {dealDocs.map((doc) => (
                  <div
                    key={doc.id}
                    onClick={() => handleDocDownload(doc)}
                    className="bg-card border border-border rounded-lg p-6 cursor-pointer group hover:border-primary/40 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-foreground transition mb-1">
                          {doc.title.replace(/\.pdf$/i, '')}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {doc.mime_type === 'application/pdf' ? 'PDF' : doc.mime_type}
                          {doc.file_size_bytes ? ` · ${formatFileSize(doc.file_size_bytes)}` : ''}
                        </p>
                      </div>
                      <span className="text-md font-medium group-hover:text-primary transition-colors">Download →</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No documents available at this time.
              </div>
            )}

            <div className="mt-12 text-center">
              <Button
                onClick={handleRequestAccess}
                size="lg"
                className="px-10 py-6 bg-[#F9EF23] hover:bg-[#F9EF23]/90 text-[#242424] text-lg font-semibold rounded-xl"
              >
                Request Access
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Financials Tab */}
      {activeTab === 'financials' && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">Investment Calculator</h2>
              <p className="text-xl text-muted-foreground">
                Explore potential returns based on your investment amount and hold period
              </p>
            </div>

            {/* Calculator Inputs */}
            <div className="bg-card border border-border rounded-lg p-8 mb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                {/* Investment Amount */}
                <div className="grid grid-rows-[auto_1fr_auto]">
                  {/* Top label */}
                  <label className="block text-sm font-semibold text-muted-foreground mb-3">
                    Investment Amount
                  </label>

                  {/* Center input */}
                  <div className="relative flex items-center">
                    <DollarSign className="absolute left-4 w-5 h-5 text-muted-foreground" />
                    <input
                      type="number"
                      min="50000"
                      step="10000"
                      value={investmentAmount}
                      onChange={(e) =>
                        setInvestmentAmount(parseInt(e.target.value) || 50000)
                      }
                      className="w-full pl-12 pr-4 py-4 bg-background border-2 border-border rounded-xl text-foreground text-xl font-bold focus:border-[#F9EF23] focus:outline-none transition"
                    />
                  </div>

                  {/* Bottom helper text */}
                  <p className="text-sm text-muted-foreground mt-2">
                    Minimum: $50,000
                  </p>
                </div>

                {/* Hold Period */}
                <div className="grid grid-rows-[auto_1fr_auto]">
                  {/* Top label */}
                  <label className="block text-sm font-semibold text-muted-foreground mb-3">
                    Hold Period (Years)
                  </label>

                  {/* Center slider */}
                  <div className="flex items-center">
                    <input
                      type="range"
                      min="1"
                      max="15"
                      value={holdPeriod}
                      onChange={(e) => setHoldPeriod(parseInt(e.target.value))}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-[#F9EF23]"
                    />
                  </div>

                  {/* Bottom scale */}
                  <div className="flex justify-between mt-2">
                    <span className="text-sm text-muted-foreground">1 yr</span>
                    <span className="text-xl font-bold text-foreground">
                      {holdPeriod} years
                    </span>
                    <span className="text-sm text-muted-foreground">15 yrs</span>
                  </div>
                </div>

              </div>
            </div>


            {/* Projections Table */}
            <div className="bg-card border border-border rounded-lg p-8 mb-12">
              <h3 className="text-2xl font-display font-bold text-foreground mb-6">
                Projected Returns ({holdPeriod}-Year Hold)
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-4 px-4 text-muted-foreground font-semibold">Scenario</th>
                      <th className="text-right py-4 px-4 text-muted-foreground font-semibold">Annual Growth</th>
                      <th className="text-right py-4 px-4 text-muted-foreground font-semibold">Projected Value</th>
                      <th className="text-right py-4 px-4 text-muted-foreground font-semibold">Total Gain</th>
                      <th className="text-right py-4 px-4 text-muted-foreground font-semibold">Total Return</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border hover:bg-muted/50 transition">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-green-600" />
                          <span className="font-semibold text-foreground">Bull Case</span>
                        </div>
                      </td>
                      <td className="text-right py-4 px-4 text-green-600 font-semibold">20%</td>
                      <td className="text-right py-4 px-4 text-foreground font-bold text-lg">
                        ${projections.bull.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="text-right py-4 px-4 text-green-600 font-semibold">
                        +${projections.bull.gain.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="text-right py-4 px-4 text-green-600 font-bold">
                        +{projections.bull.return.toFixed(0)}%
                      </td>
                    </tr>
                    <tr className="border-b border-border hover:bg-muted/50 transition bg-[#F0F0F0]/50">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-[#242424]" />
                          <span className="font-semibold text-foreground">Base Case</span>
                        </div>
                      </td>
                      <td className="text-right py-4 px-4 text-[#242424] font-semibold">17%</td>
                      <td className="text-right py-4 px-4 text-foreground font-bold text-lg">
                        ${projections.base.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="text-right py-4 px-4 text-[#242424] font-semibold">
                        +${projections.base.gain.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="text-right py-4 px-4 text-[#242424] font-bold">
                        +{projections.base.return.toFixed(0)}%
                      </td>
                    </tr>
                    <tr className="hover:bg-muted/50 transition">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <TrendingDown className="w-5 h-5 text-muted-foreground" />
                          <span className="font-semibold text-foreground">Bear Case</span>
                        </div>
                      </td>
                      <td className="text-right py-4 px-4 text-muted-foreground font-semibold">12%</td>
                      <td className="text-right py-4 px-4 text-foreground font-bold text-lg">
                        ${projections.bear.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="text-right py-4 px-4 text-muted-foreground font-semibold">
                        +${projections.bear.gain.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </td>
                      <td className="text-right py-4 px-4 text-muted-foreground font-bold">
                        +{projections.bear.return.toFixed(0)}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Investment Highlights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {investmentHighlights.map((item, i) => (
                <div key={i} className="bg-card border border-border rounded-lg p-6 text-center">
                  <p className="text-sm text-muted-foreground mb-2">{item.label}</p>
                  <p className="text-4xl md:text-5xl font-medium text-primary mb-1">{item.value}</p>
                  <p className="text-sm text-muted-foreground">{item.sub}</p>
                </div>
              ))}
            </div>

            {/* Disclaimer */}
            <div className="bg-muted/50 rounded-lg p-6 border border-border">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong className="text-foreground">Disclaimer:</strong> Projections are illustrative and based on fund targets.
                Actual returns depend on property performance, market conditions, and other factors. Past performance is not
                indicative of future results. All investments carry risk, including the possible loss of principal.
              </p>
            </div>

            <div className="mt-12 text-center">
              <Button
                onClick={handleRequestAccess}
                size="lg"
                className="px-10 py-6 bg-[#F9EF23] hover:bg-[#F9EF23]/90 text-[#242424] text-lg font-semibold rounded-xl"
              >
                Start Your Investment
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* How It Works Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">{overrides.howItWorksHeading || 'How It Works'}</h2>
            <p className="text-xl text-muted-foreground">
              {overrides.howItWorksSubheading || 'Your journey from onboarding to earning quarterly dividends'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {investmentSteps.map((step, index) => (
              <div key={index} className="bg-card border border-border rounded-lg p-8 hover:bg-primary/15 transition-colors text-center">
                <div className="text-4xl md:text-5xl font-medium text-primary mb-4">
                  {(step as any).number || (index + 1)}
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button
              onClick={handleRequestAccess}
              size="lg"
              className="px-10 py-6 bg-[#F9EF23] hover:bg-[#F9EF23]/90 text-[#242424] text-lg font-semibold rounded-xl"
            >
              Get Started Now
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#F9EF23]">
        <div className="max-w-4xl mx-auto text-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-[#242424] mb-6">
              {overrides.ctaHeading || 'Ready to Join The Hive?'}
            </h2>
            <p className="text-xl text-[#242424]/80 mb-10 max-w-2xl mx-auto">
              {overrides.ctaBody || 'Start building passive income with our diversified short-term rental portfolio. First round of The Hive Fund is now open for investment.'}
            </p>
            <Button
              onClick={handleRequestAccess}
              size="lg"
              className="px-12 py-6 bg-[#242424] text-white hover:bg-[#242424]/90 text-xl font-semibold rounded-xl shadow-lg"
            >
              Request Access
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#242424] text-white/80 py-12 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-7xl mx-auto text-center">
          <p className="mb-4">© 2026 {TENANT.branding.name}. All rights reserved.</p>
          <p className="text-sm text-white/60">
            {overrides.footerDisclaimer || 'The Hive Fund is a Reg D offering. This is not an offer to sell securities. All investments carry risk. Past performance is not indicative of future results.'}
          </p>
        </div>
      </footer>

      <KycGateModal />
    </div>
  );
}

// Default export for backward compatibility
const HiveFund: React.FC = () => <HiveFundPage />;
export default HiveFund;
